import React, { useState } from 'react';
import axios from 'axios';
import { Share2, Loader2, Copy, CheckCircle2, Twitter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareBuildModal({ open, onOpenChange, sessionId, hasProject, projectName }) {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setShareUrl('');
      setCopied(false);
      return;
    }
    if (!hasProject) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.post(`${API}/build/${sessionId}/share`, {}, { withCredentials: true });
        const shareId = r.data?.share_id;
        if (!cancel && shareId) {
          setShareUrl(`${window.location.origin}/share/${shareId}`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not create share link');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, sessionId, hasProject]);

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const tweetUrl = shareUrl
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${projectName || 'my AI build'} — built with Transformerus`)}&url=${encodeURIComponent(shareUrl)}`
    : '#';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-build-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-cyan-500" /> Share this build
          </DialogTitle>
          <DialogDescription>
            Anyone with this link can view the live preview, the code, and the chat that produced it. No sign-in needed.
          </DialogDescription>
        </DialogHeader>

        {!hasProject ? (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
            Generate something first — then come back to share it.
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Creating share link…
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={shareUrl}
                readOnly
                onClick={(e) => e.target.select()}
                data-testid="share-url-input"
                className="h-9 text-sm font-mono"
              />
              <Button
                onClick={copyLink}
                disabled={!shareUrl}
                data-testid="share-copy-btn"
                className="h-9 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white"
              >
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="flex gap-2">
              <a href={tweetUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button variant="outline" className="w-full h-9 rounded-lg text-xs">
                  <Twitter className="w-3.5 h-3.5 mr-1.5" /> Share on X
                </Button>
              </a>
              <a href={shareUrl || '#'} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button variant="outline" className="w-full h-9 rounded-lg text-xs">
                  Preview share page
                </Button>
              </a>
            </div>

            <p className="text-[11px] text-neutral-500 text-center">
              Your name's first name will be shown as the builder. The chat transcript will be visible.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
